import moment from 'moment-timezone';

/**
 * Formats a count with a singular/plural unit label.
 */
const formatCountWithUnit = (count: number, unit: string): string => `${count} ${unit}${count === 1 ? '' : 's'}`;

/**
 * Formats a month-based distance using whole calendar months plus leftover weeks.
 */
const formatMonthsAndWeeks = (now: moment.Moment, future: moment.Moment): string => {
    const months = future.diff(now, 'months');
    const monthAnchor = now.clone().add(months, 'months');
    const remainingWeeks = Math.floor(moment.duration(future.diff(monthAnchor)).asWeeks());

    if (remainingWeeks <= 0) {
        return formatCountWithUnit(months, 'month');
    }

    return `${formatCountWithUnit(months, 'month')} and ${formatCountWithUnit(remainingWeeks, 'week')}`;
};

/**
 * Formats a future timestamp as a compact human-readable relative distance.
 */
const formatTimeToFutureTimestamp = (futureTimestamp: number): string => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = moment.tz(tz);
    const future = moment.tz(futureTimestamp, tz);
    const diff = future.diff(now);

    if (diff <= 0) return 'already happened';

    const dur = moment.duration(diff);
    const yearsUntil = future.diff(now, 'years');
    const monthsUntil = future.diff(now, 'months');

    if (yearsUntil > 0) {
        return formatCountWithUnit(yearsUntil, 'year');
    }
    if (monthsUntil > 0) {
        return formatMonthsAndWeeks(now, future);
    }
    if (Math.floor(dur.asWeeks()) > 0) {
        const weeks = Math.floor(dur.asWeeks());
        return formatCountWithUnit(weeks, 'week');
    }
    if (dur.days() > 0) {
        return formatCountWithUnit(dur.days(), 'day');
    }
    if (dur.hours() > 0) {
        return formatCountWithUnit(dur.hours(), 'hour');
    }
    if (dur.minutes() > 0) {
        return formatCountWithUnit(dur.minutes(), 'minute');
    }
    return formatCountWithUnit(dur.seconds(), 'second');
};

export default formatTimeToFutureTimestamp;
